/**
 * Email Text Blocks Service
 *
 * Data access layer for email_text_blocks_codes table.
 */

import { supabase, handleSupabaseError } from '../lib/supabase';

export interface EmailTextBlock {
  id: string;
  merchant_id: string | null;
  location_id: string | null;
  email_type: number;
  language: string | null;
  preheader_text: string | null;
  url: string | null;
  logo: string | null;
  intro: string | null;
  hi_hello: string | null;
  color: string | null;
  header1: string | null;
  image: string | null;
  location_address: string | null;
  generic_block1: string | null;
  term_for_gate: string | null;
  valid_from: string | null;
  valid_until: string | null;
  generic_block2: string | null;
  header2: string | null;
  generic_block3: string | null;
  generic_block4: string | null;
  header3: string | null;
  generic_block5: string | null;
  header4: string | null;
  generic_block6: string | null;
  extend_order_text: string | null;
  extend_order_app: string | null;
  extend_order_button: string | null;
  header5: string | null;
  generic_block7: string | null;
  take_picture_text: string | null;
  return_app: string | null;
  take_picture_button: string | null;
  generic_block8: string | null;
  cancel_booking_text: string | null;
  cancel_app: string | null;
  cancel_booking_button: string | null;
  download_receipt_text: string | null;
  download_terms_text: string | null;
  subject: string | null;
  location_name: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface EmailTextBlockInsert {
  merchant_id?: string | null;
  location_id?: string | null;
  email_type: number;
  language?: string | null;
  [key: string]: any;
}

export interface EmailTextBlockUpdate {
  [key: string]: any;
}

/**
 * Fetch email text blocks by location and type
 */
export async function getEmailTextBlockByLocationAndType(
  locationId: string,
  emailType: number,
  language: string = 'en'
) {
  try {
    const { data, error } = await supabase
      .from('email_text_blocks_codes')
      .select('*')
      .eq('location_id', locationId)
      .eq('email_type', emailType)
      .eq('language', language)
      .maybeSingle();

    if (error) throw error;

    return { data: data as EmailTextBlock | null, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Create a new email text block
 */
export async function createEmailTextBlock(block: EmailTextBlockInsert) {
  try {
    const { data, error } = await supabase
      .from('email_text_blocks_codes')
      .insert(block)
      .select()
      .single();

    if (error) throw error;

    return { data: data as EmailTextBlock, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Update an email text block
 */
export async function updateEmailTextBlock(id: string, updates: EmailTextBlockUpdate) {
  try {
    const { data, error } = await supabase
      .from('email_text_blocks_codes')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    return { data: data as EmailTextBlock, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Get distinct languages that have existing email text blocks for a location
 */
export async function getExistingEmailLanguages(locationId: string) {
  try {
    const { data, error } = await supabase
      .from('email_text_blocks_codes')
      .select('language')
      .eq('location_id', locationId);

    if (error) throw error;

    const languages = [...new Set((data || []).map(row => row.language).filter(Boolean))] as string[];
    return { data: languages, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Delete an email text block
 */
export async function deleteEmailTextBlock(id: string) {
  try {
    const { error } = await supabase
      .from('email_text_blocks_codes')
      .delete()
      .eq('id', id);

    if (error) throw error;

    return { error: null };
  } catch (error) {
    return { error: handleSupabaseError(error) };
  }
}
